import { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Loader2, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("reset_token");
  
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(!!resetToken);
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    newPassword: "",
    confirmPassword: ""
  });

  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success("Account created successfully!");
      }
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email
      });
      
      if (response.data.email_sent) {
        toast.success("Password reset link sent to your email!");
      } else {
        toast.success("If an account exists, you'll receive a reset link.");
      }
      
      setResetSuccess(true);
    } catch (error) {
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: formData.newPassword
      });
      
      toast.success("Password reset successfully! You can now log in.");
      setShowResetPassword(false);
      setIsLogin(true);
      navigate("/auth");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Reset Password Form (when user clicks reset link)
  if (showResetPassword && resetToken) {
    return (
      <div className="min-h-screen flex" data-testid="auth-page">
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12 bg-white">
          <div className="max-w-md w-full mx-auto">
            <div className="mb-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_d376b3b4-e712-4a08-ace0-a95651d952ab/artifacts/jkta9457_OneWay.png" 
                alt="OneWay" 
                className="h-[100px]"
              />
            </div>

            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-[#2D3748] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Reset your password
              </h1>
              <p className="text-[#94A3B8]">
                Enter your new password below
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-[#555F67] font-medium">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#555F67] font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                    required
                    minLength={8}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38] font-medium transition-colors"
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Reset Password
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => {
                  setShowResetPassword(false);
                  navigate("/auth");
                }}
                className="text-[#4A728D] hover:text-[#355870] font-medium transition-colors flex items-center justify-center gap-2 mx-auto"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </button>
            </div>
          </div>
        </div>

        <div className="hidden lg:block lg:w-1/2 relative bg-[#F3F1EC]">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://images.unsplash.com/photo-1544654262-e295983be0f4?crop=entropy&cs=srgb&fm=jpg&q=85')`,
              filter: 'brightness(0.9)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#152A38]/60 to-transparent" />
        </div>
      </div>
    );
  }

  // Forgot Password Form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex" data-testid="auth-page">
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12 bg-white">
          <div className="max-w-md w-full mx-auto">
            <div className="mb-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_d376b3b4-e712-4a08-ace0-a95651d952ab/artifacts/jkta9457_OneWay.png" 
                alt="OneWay" 
                className="h-[100px]"
              />
            </div>

            {resetSuccess ? (
              <div className="text-center">
                <div className="mb-6">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                </div>
                <h1 className="text-3xl font-semibold text-[#2D3748] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Check your email
                </h1>
                <p className="text-[#94A3B8] mb-8">
                  We've sent a password reset link to <strong className="text-[#555F67]">{formData.email}</strong>
                </p>
                <p className="text-sm text-[#94A3B8] mb-6">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <Button
                  onClick={() => {
                    setResetSuccess(false);
                    setShowForgotPassword(false);
                  }}
                  variant="outline"
                  className="border-[#A3C3D8] text-[#4A728D]"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <h1 className="text-3xl font-semibold text-[#2D3748] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Forgot password?
                  </h1>
                  <p className="text-[#94A3B8]">
                    No worries, we'll send you reset instructions
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#555F67] font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10 h-11 border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38] font-medium transition-colors"
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Send Reset Link
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="text-[#4A728D] hover:text-[#355870] font-medium transition-colors flex items-center justify-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Sign In
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="hidden lg:block lg:w-1/2 relative bg-[#F3F1EC]">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://images.unsplash.com/photo-1544654262-e295983be0f4?crop=entropy&cs=srgb&fm=jpg&q=85')`,
              filter: 'brightness(0.9)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#152A38]/60 to-transparent" />
        </div>
      </div>
    );
  }

  // Main Login/Register Form
  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_d376b3b4-e712-4a08-ace0-a95651d952ab/artifacts/jkta9457_OneWay.png" 
              alt="OneWay" 
              className="h-[100px]"
              data-testid="auth-logo"
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-[#2D3748] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isLogin ? "Welcome back" : "Create account"}
            </h1>
            <p className="text-[#94A3B8]">
              {isLogin 
                ? "Sign in to continue tracking your job applications" 
                : "Start organizing your job search today"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2 animate-slide-up">
                <Label htmlFor="name" className="text-[#555F67] font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#555F67] font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-11 border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#555F67] font-medium">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#4A728D] hover:text-[#355870] font-medium transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 h-11 border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38] font-medium transition-colors"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {isLogin ? "Sign In" : "Create Account"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          {/* Toggle */}
          <div className="mt-8 text-center">
            <p className="text-[#94A3B8]">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#4A728D] hover:text-[#355870] font-medium transition-colors"
                data-testid="auth-toggle-btn"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-[#F3F1EC]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1544654262-e295983be0f4?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            filter: 'brightness(0.9)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#152A38]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <blockquote className="text-white">
            <p className="text-2xl font-medium mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              "Organize your job search with clarity and focus."
            </p>
            <footer className="text-white/80">
              — OneWay, Your job tracking companion
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
